import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import { subYears, addDays } from 'date-fns';

import { IFormImperativeHandleProps } from '../../../../../common/form';
import { BirthdayModalProps } from './birthday-modal.models';
import { yup } from '../../../../../../services/yup';
import { TFormValues } from '../../../vision-profile/vision-profile-views/birthday-view/birthday-view-form';
import { ReactComponent as BirthdayPieImage } from '../../../../../../assets/images/kids/pie.svg';
import { useStyles } from './birthday-modal.styles';
import { BirthdayViewForm } from '../../../vision-profile/vision-profile-views/birthday-view/birthday-view-form';
import { Modal } from '../../../../../common';
import { v2025PP16783 } from '../../../../../../constants/veeva';
import { toDate } from '../../../../../../utils/date/to-date';

export const BirthdayModal = ({
  onClose,
  onSetDate,
  dateDefault,
  ...props
}: BirthdayModalProps): JSX.Element => {
  const ref = useRef<IFormImperativeHandleProps>(null);
  const intl = useIntl();
  const classes = useStyles();
  const { palette } = useTheme();
  const [value, setValue] = useState<Date | null>(dateDefault ? new Date(dateDefault) : null);
  const minDate = useMemo(() => toDate(addDays(subYears(new Date(), 18), 1)), [])!;
  const defaultValues = useMemo(
    () => ({ dateOfBirth: dateDefault ? new Date(dateDefault) : null }),
    [dateDefault],
  );

  const validationSchema = useMemo(
    () =>
      yup.object({
        dateOfBirth: yup
          .date()
          .nullable()
          .required(intl.formatMessage({ id: 'common.errors.required.long' })),
      }),
    [intl],
  );

  const handleBirthdayChange = async ({ dateOfBirth }: TFormValues) => {
    if (!dateOfBirth) {
      return;
    }

    onSetDate(dateOfBirth);
    onClose!();
  };

  const onChangeValue = (date: Date) => {
    setValue(date);
  };

  return (
    <Modal
      {...props}
      onClose={onClose}
      veevaNumber={v2025PP16783}
      content={
        <>
          <h2 className={classes.title}>
            {intl.formatMessage({ id: 'profile.birthday.modal.title' }, { noBreak: <>&nbsp;</> })}
          </h2>
          <div className={classes.pieImage} data-testid="birthday-pie-image">
            <BirthdayPieImage />
          </div>
          <BirthdayViewForm
            ref={ref}
            formProps={{
              onSubmit: handleBirthdayChange,
              defaultValues,
              validationSchema,
              reValidateMode: 'onChange',
            }}
            canUpdateDateOfBirth
            isAction
            isDisabled={!value}
            getValue={onChangeValue}
            maxDateOfBirth={new Date()}
            minDate={minDate}
            floatingLabel={
              <>
                {intl.formatMessage({ id: 'profile.dateOfBirthTextField.label' })}
                <span style={{ color: palette.error.main }}>*</span>
              </>
            }
            disclaimer={
              value && (
                <p className={classes.disclaimer}>
                  {intl.formatMessage({ id: 'createKidsProfile.date.disclaimer' })}
                </p>
              )
            }
          />
        </>
      }
    />
  );
};
